import React, { useState, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Checkbox,
  TableSortLabel,
  Skeleton,
  Button
} from '@mui/material'
import { 
  ChevronLeft, 
  ChevronRight, 
  KeyboardDoubleArrowLeft, 
  KeyboardDoubleArrowRight,
  FilterList as FilterListIcon,
  Inbox as EmptyIcon
} from '@mui/icons-material'

/**
 * CommonDataGrid Component
 * Standardized grid with pagination and dynamic controls.
 * 
 * @param {Array} columns - Array of { key, label, render } objects
 * @param {Array} data - Array of data objects
 * @param {Function} onRowClick - Single click handler
 * @param {Function} onRowDoubleClick - Double click handler
 * @param {string|number} selectedRowId - ID of currently selected row (single selection highlight)
 * @param {Array} selectedIds - Array of IDs for multi-selection (checkboxes)
 * @param {Function} onSelectionChange - Handler for multi-selection change
 * @param {boolean} selectable - Whether to show checkboxes
 * @param {string} idField - Field name for unique ID (default: productId)
 * @param {number} rowsPerPage - Rows per page (default: 10)
 * @param {string} height - Height of the container
 */
const CommonDataGrid = ({ 
  columns, 
  data = [], 
  onRowClick, 
  onRowDoubleClick,
  selectedRowId,
  selectedIds = [],
  onSelectionChange,
  selectable = false,
  idField = 'productId',
  rowsPerPage = 10,
  height = '100%',
  loading = false,
  onRetry
}) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

  // Reset to first page if data changes (e.g. filtering)
  useEffect(() => {
    setCurrentPage(0)
  }, [data.length])

  // Sorting Logic
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data

    const activeColumn = columns.find(col => col.key === sortConfig.key)

    const sorted = [...data].sort((a, b) => {
      let aValue = activeColumn?.sortValue ? activeColumn.sortValue(a) : a[sortConfig.key]
      let bValue = activeColumn?.sortValue ? activeColumn.sortValue(b) : b[sortConfig.key]

      if (aValue === bValue) return 0
      
      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      // Numeric sort
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      // String sort (case insensitive)
      const aString = String(aValue).toLowerCase()
      const bString = String(bValue).toLowerCase()
      
      if (aString < bString) return sortConfig.direction === 'asc' ? -1 : 1
      if (aString > bString) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [data, sortConfig, columns])

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') direction = 'desc'
      else if (sortConfig.direction === 'desc') direction = null
    }
    setSortConfig({ key: direction ? key : null, direction: direction || 'asc' })
  }

  const startIndex = currentPage * rowsPerPage
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length)
  const currentPageData = sortedData.slice(startIndex, endIndex)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const firstPage = () => setCurrentPage(0)
  const lastPage = () => setCurrentPage(totalPages - 1)
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 0))

  const handleSelectAll = (event) => {
    if (onSelectionChange) {
      if (event.target.checked) {
        onSelectionChange(data.map(row => row[idField]))
      } else {
        onSelectionChange([])
      }
    }
  }

  const handleSelectRow = (event, id) => {
    event.stopPropagation()
    if (onSelectionChange) {
      const selectedIndex = selectedIds.indexOf(id)
      let newSelected = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedIds, id)
      } else {
        newSelected = newSelected.filter(item => item !== id)
      }
      onSelectionChange(newSelected)
    }
  }

  const showPagination = data.length > 0
  const showArrows = data.length > rowsPerPage

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '4px 4px 0 0'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell 
                  padding="checkbox" 
                  sx={{ 
                    backgroundColor: '#f5f7fa',
                    borderBottom: '2px solid #e0e0e0'
                  }}
                >
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                    checked={data.length > 0 && selectedIds.length === data.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
              )}
              {columns.map(col => (
                <TableCell 
                  key={col.key} 
                  sortDirection={sortConfig.key === col.key ? sortConfig.direction : false}
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#f5f7fa',
                    color: '#344767',
                    borderBottom: '2px solid #e0e0e0',
                    whiteSpace: 'nowrap',
                    padding: '8px 16px'
                  }}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={sortConfig.key === col.key}
                      direction={sortConfig.key === col.key ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(col.key)}
                      sx={{
                        '&.Mui-active': { color: '#1e40af' },
                        '&.Mui-active .MuiTableSortLabel-icon': { color: '#1e40af' }
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading ? (
              currentPageData.map((row, index) => {
                const rowId = row[idField]
                const isSelected = selectedIds.indexOf(rowId) !== -1 || selectedRowId === rowId

                return (
                  <TableRow
                    key={rowId || `${index}-${startIndex}`}
                    hover
                    onClick={() => onRowClick && onRowClick(row)}
                    onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
                    selected={isSelected}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fcfcfc' },
                      cursor: 'pointer',
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd !important',
                        '&:hover': {
                          backgroundColor: '#bbdefb !important'
                        }
                      }
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.indexOf(rowId) !== -1}
                          onChange={(event) => handleSelectRow(event, rowId)}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {columns.map(col => (
                      <TableCell key={col.key} sx={{ color: '#4a5568', whiteSpace: 'nowrap' }}>
                        {col.render 
                          ? col.render(row) 
                          : (row[col.key] !== undefined ? String(row[col.key]) : '—')
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            ) : (
              [...Array(5)].map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={18} height={18} />
                    </TableCell>
                  )}
                  {columns.map((col, colIndex) => (
                    <TableCell key={`skeleton-col-${colIndex}`}>
                      <Skeleton variant="text" width={colIndex % 2 === 0 ? '80%' : '60%'} height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {!loading && (sortedData.length === 0) && (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center" sx={{ py: 10 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <EmptyIcon sx={{ fontSize: 64, color: '#e2e8f0' }} />
                    <Box>
                      <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 600 }}>
                        No records found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Try adjusting your filters or search terms
                      </Typography>
                    </Box>
                    {onRetry && (
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={onRetry}
                        sx={{ mt: 1, textTransform: 'none', borderRadius: '8px' }}
                      >
                        Refresh Data
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <Box 
          sx={{ 
            p: 1.5, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px'
          }}
        >
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
            Showing {sortedData.length > 0 ? startIndex + 1 : 0} to {endIndex} of {sortedData.length} entries
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={firstPage} disabled={currentPage === 0}>
              <KeyboardDoubleArrowLeft />
            </IconButton>
            <IconButton size="small" onClick={prevPage} disabled={currentPage === 0}>
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', px: 2 }}>
              Page {totalPages > 0 ? currentPage + 1 : 0} of {totalPages}
            </Typography>

            <IconButton size="small" onClick={nextPage} disabled={currentPage >= totalPages - 1 || totalPages === 0}>
              <ChevronRight />
            </IconButton>
            <IconButton size="small" onClick={lastPage} disabled={currentPage >= totalPages - 1 || totalPages === 0}>
              <KeyboardDoubleArrowRight />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CommonDataGrid
